// AIBrowserAgent.js - AI-powered browser automation agent that operates websites like a human
import React, { useState, useRef, useEffect } from 'react';
import './AIBrowserAgent.css';
import SandboxBrowser from './SandboxBrowser';

const SENSITIVE_FIELDS = [
  'password', 'passwd', 'pwd', 'pass',
  'email', 'e-mail', 'mail',
  'credit', 'card', 'cvv', 'cvc', 'ccv',
  'bank', 'account', 'routing',
  'ssn', 'social security',
  'pin', 'security code'
];

export default function AIBrowserAgent({ isOpen, onClose, initialUrl = '', initialTask = '', onTaskResult = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(initialUrl || 'https://www.google.com');
  const [currentTask, setCurrentTask] = useState(initialTask || null);
  const [executionLog, setExecutionLog] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [aiThinking, setAiThinking] = useState('');
  const [cursorPosition, setCursorPosition] = useState({ x: 50, y: 50 });
  const [cursorVisible, setCursorVisible] = useState(true);
  const [cursorClick, setCursorClick] = useState(false);
  const messagesEndRef = useRef(null);
  const browserRef = useRef(null);
  const browserFrameRef = useRef(null);
  const lastAiMessageRef = useRef('');

  // Open website by itself when panel opens with initialUrl
  useEffect(() => {
    if (!isOpen) return;
    const u = (initialUrl || '').trim();
    if (u) {
      const withProtocol = u.startsWith('http://') || u.startsWith('https://') ? u : 'https://' + u;
      setCurrentUrl(withProtocol);
      setTimeout(() => {
        browserRef.current?.navigateTo?.(withProtocol);
      }, 100);
    }
  }, [isOpen, initialUrl]);

  // Auto-start if initial task is provided (e.g. from main chatbot)
  useEffect(() => {
    if (!isOpen || !initialTask || isProcessing) return;
    setCurrentTask(initialTask);
    setTimeout(() => {
      processCommand(initialTask);
    }, 800);
  }, [isOpen, initialTask]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addMessage = (role, content) => {
    if (role === 'ai') lastAiMessageRef.current = content;
    setMessages(prev => [...prev, {
      id: Date.now(),
      role,
      content,
      timestamp: Date.now()
    }]);
  };

  const addExecutionLog = (step, status = 'info') => {
    setExecutionLog(prev => [...prev, {
      id: Date.now(),
      step,
      status, // info, success, error, warning
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const isSensitiveField = (fieldName) => {
    const lower = (fieldName || '').toLowerCase();
    return SENSITIVE_FIELDS.some(sensitive => lower.includes(sensitive));
  };

  const simulateBrowserAction = async (action, details) => {
    // Move virtual cursor to center of sandbox frame (control the sandbox screen visually)
    const frameRect = browserRef.current?.getFrameRect?.();
    const frameEl = browserFrameRef.current;
    if (frameRect && frameEl && cursorVisible) {
      const parentRect = frameEl.getBoundingClientRect();
      const x = frameRect.left - parentRect.left + frameRect.width / 2 - 12;
      const y = frameRect.top - parentRect.top + frameRect.height / 2 - 12;
      setCursorPosition({ x: Math.max(0, x), y: Math.max(0, y) });
      if (action === 'navigate' || action === 'click' || action === 'search') {
        setCursorClick(true);
        setTimeout(() => setCursorClick(false), 300);
      }
    }
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));

    if (action === 'fill_input' && details.fieldName) {
      if (isSensitiveField(details.fieldName)) {
        throw new Error(`🔒 Cannot fill sensitive field: "${details.fieldName}". This requires manual user input for security.`);
      }
    }

    return { success: true, action, details };
  };

  const processCommand = async (command) => {
    setIsProcessing(true);
    setIsExecuting(true);
    if (messages.length > 0) {
      addMessage('user', command);
    }

    const lowerCmd = command.toLowerCase();

    try {
      setAiThinking('🤖 Analyzing your request...');
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate to website
      if (lowerCmd.includes('open') || lowerCmd.includes('go to') || lowerCmd.includes('visit')) {
        setAiThinking('🌐 Finding the website...');
        const urlMatch = command.match(/(https?:\/\/[^\s]+)|([a-zA-Z0-9-]+\.(com|org|net|edu|gov|io|co|ai|app)[^\s]*)/i);
        let targetUrl = urlMatch ? urlMatch[0] : null;

        if (!targetUrl) {
          // Extract domain names
          const domainMatch = command.match(/\b([a-zA-Z0-9-]+)\s*(website|site|\.com|\.org|\.net)?\b/i);
          if (domainMatch) {
            targetUrl = domainMatch[1].includes('.') ? domainMatch[1] : `${domainMatch[1]}.com`;
          }
        }

        if (targetUrl) {
          const finalUrl = targetUrl.startsWith('http') ? targetUrl : `https://${targetUrl}`;
          addExecutionLog('🌐 Navigating to website...', 'info');
          browserRef.current?.navigateTo?.(finalUrl);
          await simulateBrowserAction('navigate', { url: finalUrl });
          setCurrentUrl(finalUrl);
          addExecutionLog(`✅ Opened ${finalUrl}`, 'success');
          addMessage('ai', `✅ Successfully opened ${finalUrl}. What would you like me to do on this site?`);
        } else {
          addMessage('ai', '❓ I couldn\'t find a URL in your command. Please specify the website (e.g., "Open google.com")');
        }
      }
      // Search functionality
      else if (lowerCmd.includes('search for') || lowerCmd.includes('find') || lowerCmd.includes('look for')) {
        const searchQuery = command.replace(/search for|find|look for/gi, '').trim();
        addExecutionLog('🔍 Performing search...', 'info');
        await simulateBrowserAction('search', { query: searchQuery });
        addExecutionLog('📊 Analyzing search results...', 'info');
        await new Promise(resolve => setTimeout(resolve, 1000));
        addExecutionLog(`✅ Found results for "${searchQuery}"`, 'success');
        addMessage('ai', `✅ I've searched for "${searchQuery}". Would you like me to:\n• Click on a specific result\n• Read the page content\n• Extract information`);
      }
      // Fill form
      else if (lowerCmd.includes('fill') || lowerCmd.includes('complete form') || lowerCmd.includes('enter')) {
        addExecutionLog('📝 Analyzing form fields...', 'info');
        await simulateBrowserAction('analyze_form', {});

        addExecutionLog('🔍 Identifying safe fields to fill...', 'info');
        await new Promise(resolve => setTimeout(resolve, 800));

        // Simulate finding fields (excluding sensitive ones)
        const mockFields = [
          { name: 'First Name', value: 'John', safe: true },
          { name: 'Last Name', value: 'Doe', safe: true },
          { name: 'Phone', value: '555-0123', safe: true },
          { name: 'Email', value: '[USER INPUT REQUIRED]', safe: false },
          { name: 'Password', value: '[USER INPUT REQUIRED]', safe: false }
        ];

        for (const field of mockFields) {
          if (field.safe) {
            addExecutionLog(`✍️ Filling "${field.name}" with "${field.value}"`, 'info');
            await simulateBrowserAction('fill_input', { fieldName: field.name, value: field.value });
          } else {
            addExecutionLog(`🔒 Skipping sensitive field: "${field.name}" (requires manual input)`, 'warning');
          }
        }

        addExecutionLog('✅ Form filling complete (non-sensitive fields only)', 'success');
        addMessage('ai', '✅ I\'ve filled all non-sensitive form fields. Please manually enter:\n🔒 Email address\n🔒 Password\n\nLet me know if you need me to submit the form after you fill these!');
      }
      // Click action
      else if (lowerCmd.includes('click') || lowerCmd.includes('press')) {
        const buttonMatch = command.match(/click\s+(?:on\s+)?["']?([^"']+)["']?/i) ||
                          command.match(/press\s+["']?([^"']+)["']?/i);
        const buttonName = buttonMatch ? buttonMatch[1] : 'button';

        addExecutionLog(`🖱️ Locating "${buttonName}"...`, 'info');
        await simulateBrowserAction('locate_element', { type: 'button', text: buttonName });

        addExecutionLog(`👆 Clicking "${buttonName}"...`, 'info');
        await simulateBrowserAction('click', { element: buttonName });

        addExecutionLog(`✅ Clicked "${buttonName}"`, 'success');
        addMessage('ai', `✅ Successfully clicked "${buttonName}". Waiting for page response...`);
      }
      // Booking/reservation
      else if (lowerCmd.includes('book') || lowerCmd.includes('reserve') || lowerCmd.includes('appointment')) {
        addExecutionLog('📅 Starting booking process...', 'info');
        await simulateBrowserAction('start_booking', {});

        addExecutionLog('🔍 Analyzing available dates and times...', 'info');
        await new Promise(resolve => setTimeout(resolve, 1500));

        addExecutionLog('📝 Filling booking information...', 'info');
        await simulateBrowserAction('fill_booking', {
          name: 'User',
          phone: '555-0123',
          date: 'Next available'
        });

        addExecutionLog('✅ Booking information prepared', 'success');
        addMessage('ai', '✅ I\'ve prepared the booking with available information. Please review and confirm:\n\n📅 Date: Next available\n⏰ Time: First slot\n📱 Phone: 555-0123\n\n🔒 Note: You\'ll need to manually enter your email and payment info if required.');
      }
      // Read/extract information
      else if (lowerCmd.includes('read') || lowerCmd.includes('extract') || lowerCmd.includes('get information')) {
        addExecutionLog('👀 Reading page content...', 'info');
        await simulateBrowserAction('read_page', {});

        addExecutionLog('🧠 Analyzing and extracting information...', 'info');
        await new Promise(resolve => setTimeout(resolve, 1200));

        addExecutionLog('✅ Information extracted', 'success');
        addMessage('ai', '✅ I\'ve analyzed the page. Here\'s what I found:\n\n📊 Key Information:\n• Main heading: [Page title]\n• Key sections: [Section summaries]\n• Links and actions: [Available options]\n\nWould you like me to interact with any specific element?');
      }
      // Screenshot
      else if (lowerCmd.includes('screenshot') || lowerCmd.includes('capture')) {
        addExecutionLog('📸 Taking screenshot...', 'info');
        await simulateBrowserAction('screenshot', {});
        addExecutionLog('✅ Screenshot captured', 'success');
        addMessage('ai', '✅ Screenshot captured! I can analyze it and tell you what\'s visible on the page.');
      }
      // Scroll action
      else if (lowerCmd.includes('scroll')) {
        const direction = lowerCmd.includes('down') ? 'down' : lowerCmd.includes('up') ? 'up' : 'down';
        addExecutionLog(`📜 Scrolling ${direction}...`, 'info');
        await simulateBrowserAction('scroll', { direction });
        addExecutionLog(`✅ Scrolled ${direction}`, 'success');
        addMessage('ai', `✅ Scrolled ${direction}. Let me know what you'd like me to do next!`);
      }
      // Help
      else if (lowerCmd.includes('help') || lowerCmd.includes('what can you')) {
        addMessage('ai', `🤖 Here's what I can do:

📍 **Navigation:**
• "Open google.com"
• "Go to amazon.com"
• "Visit youtube.com"

🔍 **Search & Find:**
• "Search for best hotels"
• "Find restaurants near me"
• "Look for cheap flights"

📝 **Form Filling:**
• "Fill the form"
• "Complete the registration"
• "Enter my details"

🖱️ **Interactions:**
• "Click the sign up button"
• "Press submit"
• "Click on the first result"

📅 **Bookings:**
• "Book an appointment"
• "Reserve a table"
• "Make a reservation"

👀 **Information:**
• "Read the page"
• "Extract information"
• "Screenshot this page"

🔒 **Privacy Protected:**
I will NEVER fill passwords, emails, bank cards, or any sensitive data. These require your manual input for security!`);
      }
      else {
        addMessage('ai', '🤔 I\'m not sure what you want me to do. Try:\n• "Open [website].com"\n• "Fill the form"\n• "Click [button name]"\n• "Search for [query]"\n• "Help" for more commands');
      }
    } catch (error) {
      addExecutionLog(`❌ Error: ${error.message}`, 'error');
      addMessage('ai', `❌ ${error.message}`);
    } finally {
      setIsProcessing(false);
      setIsExecuting(false);
      setTimeout(() => setAiThinking(''), 500);
      if (typeof onTaskResult === 'function') {
        onTaskResult({ message: lastAiMessageRef.current || 'Task completed.' });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;

    processCommand(input);
    setInput('');
  };

  const quickActions = [
    { label: '🌐 Open Website', command: 'Open ' },
    { label: '📝 Fill Form', command: 'Fill the form on this page' },
    { label: '🔍 Search', command: 'Search for ' },
    { label: '👆 Click Button', command: 'Click the ' },
    { label: '📅 Book', command: 'Make a booking' },
    { label: '👀 Read Page', command: 'Read the page content' },
  ];

  if (!isOpen) return null;

  return (
    <div className="ai-browser-agent">
      {/* Minimal Header */}
      <div className="agent-header-minimal">
        <div className="agent-status-bar">
          <span className="ai-indicator">
            🤖 AI Agent {isExecuting ? 'Working...' : 'Standby'}
          </span>
          {currentTask && (
            <span className="current-task">Task: {currentTask}</span>
          )}
        </div>
        <div className="header-controls">
          <button
            type="button"
            className={`agent-cursor-toggle ${cursorVisible ? 'on' : ''}`}
            onClick={() => setCursorVisible(v => !v)}
            title={cursorVisible ? 'Hide virtual cursor' : 'Show virtual cursor'}
            aria-label={cursorVisible ? 'Hide virtual cursor' : 'Show virtual cursor'}
          >
            <span className="cursor-toggle-icon" aria-hidden>⌖</span>
          </button>
          <button
            className="toggle-controls-btn"
            onClick={() => setShowControls(!showControls)}
            title="Toggle Controls"
          >
            {showControls ? '◀' : '▶'}
          </button>
          <button className="agent-close-btn-minimal" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Full Screen Browser – opens website by itself via url prop; ref for imperative navigate + cursor overlay */}
      <div className="agent-browser-fullscreen" ref={browserFrameRef}>
        {isExecuting && (
          <div className="agent-controlling-badge" aria-live="polite">
            AI controlling sandbox
          </div>
        )}
        <SandboxBrowser
          ref={browserRef}
          url={currentUrl}
          onClose={null}
          takeoverMode={true}
          confirmationRequired={false}
          stealthMode={false}
        />
        {/* Virtual cursor overlay – shows AI controlling the sandbox (cursor + click feedback) */}
        {cursorVisible && (
          <div
            className="agent-virtual-cursor-wrap"
            style={{ pointerEvents: 'none' }}
            aria-hidden="true"
          >
            <div
              className={`agent-virtual-cursor ${cursorClick ? 'agent-cursor-click' : ''}`}
              style={{
                left: cursorPosition.x,
                top: cursorPosition.y,
              }}
            >
              <span className="agent-cursor-pointer" />
              {cursorClick && <span className="agent-cursor-ripple" />}
            </div>
          </div>
        )}
      </div>

      {/* Floating AI Thinking Overlay */}
      {aiThinking && isExecuting && (
        <div className="ai-thinking-overlay">
          <div className="ai-thinking-bubble">
            <div className="thinking-icon">🤖</div>
            <div className="thinking-text">{aiThinking}</div>
            <div className="thinking-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      )}

      {/* Floating Execution Log */}
      {executionLog.length > 0 && (
        <div className="floating-execution-log">
          <div className="log-header">
            <span>📋 AI Actions</span>
            <span className="log-count">{executionLog.length}</span>
          </div>
          <div className="log-entries-floating">
            {executionLog.slice(-5).reverse().map(entry => (
              <div key={entry.id} className={`log-entry-floating ${entry.status}`}>
                <span className="log-icon">
                  {entry.status === 'success' ? '✅' :
                   entry.status === 'error' ? '❌' :
                   entry.status === 'warning' ? '⚠️' : '🔵'}
                </span>
                <span className="log-step-floating">{entry.step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side Panel (when controls shown) */}
      {showControls && (
        <div className="agent-side-panel">
          <div className="side-panel-content">
            <h4>🎯 Manual Control</h4>
            <form className="agent-input-form-compact" onSubmit={handleSubmit}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Give AI a command..."
                disabled={isProcessing}
                className="agent-input-compact"
              />
              <button type="submit" disabled={isProcessing || !input.trim()} className="agent-send-btn-compact">
                ➤
              </button>
            </form>

            {/* Quick Actions */}
            <div className="quick-actions-compact">
              <h5>⚡ Quick Commands</h5>
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  className="quick-action-btn-compact"
                  onClick={() => {
                    processCommand(action.command);
                  }}
                  disabled={isProcessing}
                >
                  {action.label}
                </button>
              ))}
            </div>

            {/* Full Log */}
            <div className="full-log">
              <h5>📋 Complete Log</h5>
              <div className="log-entries-panel">
                {executionLog.map(entry => (
                  <div key={entry.id} className={`log-entry-panel ${entry.status}`}>
                    <span className="log-time">{entry.timestamp}</span>
                    <span className="log-step">{entry.step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Security Badge */}
      <div className="agent-security-badge">
        🔒 Privacy Protected
      </div>
    </div>
  );
}
